import { useState, useRef } from 'react';
import { useAuth } from '../../../auth';
import type { Room } from '@modelcast/types';
import { SwitchControl } from '../../../components/SwitchControl';
import { Modal } from '../../../components/Modal';

interface EventEditModalProps {
  event: Room;
  onClose: () => void;
  onSaved: () => void;
}

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

export function EventEditModal({ event, onClose, onSaved }: EventEditModalProps) {
  const { getToken } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [currentImageUrl, setCurrentImageUrl] = useState(event.imageUrl);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: event.name,
    description: event.description ?? '',
    scheduledAt: event.scheduledAt ? new Date(event.scheduledAt).toISOString().slice(0, 16) : '',
    duration: event.duration,
    maxAttendees: event.maxAttendees ?? '',
    isPublic: event.isPublic,
    isPublished: event.isPublished,
  });

  async function handleSave() {
    // Mandatory audit note: admin event edits touch publication state.
    const note = window.prompt('Audit note for this event edit (required):');
    if (note === null) return;
    if (!note.trim()) {
      alert('An audit note is required to edit an event.');
      return;
    }

    setIsSaving(true);
    try {
      const token = await getToken();
      const scheduledAt = formData.scheduledAt ? new Date(formData.scheduledAt) : null;
      const duration = Number(formData.duration) || event.duration || 60;
      const response = await fetch(`/api/admin/events/${event.code}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null,
          scheduledAt: scheduledAt ? scheduledAt.toISOString() : null,
          endsAt: scheduledAt ? new Date(scheduledAt.getTime() + duration * 60 * 1000).toISOString() : null,
          duration,
          maxAttendees: formData.maxAttendees ? Number(formData.maxAttendees) : null,
          isPublic: formData.isPublic,
          isPublished: formData.isPublished,
          note: note.trim(),
        }),
      });

      if (response.ok) {
        onSaved();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to save event');
      }
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Failed to save event');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadStatus('uploading');
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`/api/admin/events/${event.code}/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentImageUrl(data.imageUrl);
        setUploadStatus('success');
        setTimeout(() => setUploadStatus('idle'), 2000);
      } else {
        setUploadStatus('error');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadStatus('error');
    }
  }

  return (
    <Modal
      onClose={onClose}
      backdropClassName="bg-black/70"
      panelClassName="bg-panel border border-line rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
      closeOnBackdrop
      closeOnEscape={false}
      ariaLabel="Edit event"
    >
        {/* Header */}
        <div className="px-6 py-4 border-b border-line flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-lg font-medium text-text">Edit Event</h2>
            <code className="text-xs text-text-dim">{event.code}</code>
          </div>
          <button onClick={onClose} className="text-text-dim hover:text-text">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-5">
          {/* Event Image */}
          <div>
            <label className="block text-[10px] font-mono text-text-faint uppercaser mb-2">
              Event Image
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            <div className="flex items-start gap-3">
              {currentImageUrl ? (
                <div className="relative w-32 h-20 rounded-lg overflow-hidden bg-panel2">
                  <img src={currentImageUrl} alt="" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-32 h-20 rounded-lg bg-panel2 border border-dashed border-line flex items-center justify-center text-text-faint text-xs">
                  No image
                </div>
              )}
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadStatus === 'uploading'}
                  className="btn text-sm"
                >
                  {uploadStatus === 'uploading' ? 'Uploading...' : 'Upload Image'}
                </button>
                {uploadStatus === 'success' && (
                  <span className="text-xs text-ok">Uploaded!</span>
                )}
                {uploadStatus === 'error' && (
                  <span className="text-xs text-warn">Upload failed</span>
                )}
              </div>
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-[10px] font-mono text-text-faint uppercaser mb-2">
              Event Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
              className="input w-full"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-[10px] font-mono text-text-faint uppercaser mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
              className="input w-full h-24 resize-none"
            />
          </div>

          {/* Schedule */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono text-text-faint uppercaser mb-2">
                Scheduled Time
              </label>
              <input
                type="datetime-local"
                value={formData.scheduledAt}
                onChange={(e) => setFormData(f => ({ ...f, scheduledAt: e.target.value }))}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono text-text-faint uppercaser mb-2">
                Duration (min)
              </label>
              <input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData(f => ({ ...f, duration: parseInt(e.target.value) || 60 }))}
                min={15}
                className="input w-full"
              />
            </div>
          </div>

          {/* Attendees */}
          <div>
            <label className="block text-[10px] font-mono text-text-faint uppercaser mb-2">
              Max Attendees (blank for unlimited)
            </label>
            <input
              type="number"
              value={formData.maxAttendees}
              onChange={(e) => setFormData(f => ({ ...f, maxAttendees: e.target.value }))}
              min={1}
              className="input w-full"
            />
          </div>

          {/* Toggles */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <SwitchControl
                checked={formData.isPublic}
                onChange={(isPublic) => setFormData(f => ({ ...f, isPublic }))}
                ariaLabel="Public event"
              />
              <div>
                <span className="text-sm text-text">Public Event</span>
                <p className="text-xs text-text-dim">Listed on the homepage for anyone to join</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <SwitchControl
                checked={formData.isPublished}
                onChange={(isPublished) => setFormData(f => ({ ...f, isPublished }))}
                ariaLabel="Published"
              />
              <div>
                <span className="text-sm text-text">Published</span>
                <p className="text-xs text-text-dim">Visible to invited users (vs draft)</p>
              </div>
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-line flex justify-end gap-3 shrink-0 bg-panel">
          <button onClick={onClose} className="btn" disabled={isSaving}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !formData.name.trim()}
            className="btn btn-accent"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
    </Modal>
  );
}
